/**
 * sheetRenderer — render ONE sheet (layout) into an EXISTING `PDFDocument`.
 *
 * Extracted from `exportLayoutPdf` (PDF-4 split) so both the single-sheet PLOT
 * (`exportLayoutPdf`) and the multi-sheet PUBLISH (`publishLayouts`) share one
 * page-rendering body. `renderSheetPage` adds ONE page sized to the sheet's
 * physical paper, resolves the plot area (`resolvePlotRegion` — PDF-4), builds
 * the page transform with the resolved region origin / fit scale / centering,
 * then walks the plotted space's paper-space entities interleaved with each
 * VIEWPORT's clipped model content (PDF-3 draw-order), exactly as PDF-1/2/3 did.
 *
 * Layout-area sheets are byte-identical to the pre-PDF-4 exporter: for a Layout
 * region `resolvePlotRegion` returns origin (0,0), scale 1, centering (0,0), so
 * the transform is built with the same inputs as before (no scaleOverride /
 * centering supplied → omitted).
 */

import {
  PDFDocument,
  pushGraphicsState,
  popGraphicsState,
  moveTo,
  lineTo,
  closePath,
  clip,
  endPath,
  stroke,
  setLineWidth,
  setStrokingRgbColor,
  type PDFPage,
} from "pdf-lib";
import fontkit from "@pdf-lib/fontkit";

import {
  coerceColor,
  colorToLegacyString,
  type DrawingDocument,
  type DrawingSpace,
  type LayerDefinition,
  type LayoutPlotSettings,
  type TextStyle,
  type ViewportEntity,
} from "@dxf-ai-editor/drawing-model";
import type { PlotStyleTable } from "@dxf-ai-editor/acadctb";

import { buildLayerColorMap } from "../../scene3d/rendering/colorResolution";
import {
  buildPdfTransform,
  resolvePlotRgb,
  HAIRLINE_PT,
  MM_TO_PT,
  type PdfTransform,
  type RgbColor,
} from "./pdfTransform";
import {
  DEFAULT_PAPER_TOLERANCE_MM,
  type CadConvertContext,
  type GradientHatchPrimitive,
  type HatchPatternPrimitive,
  type PathPrimitive,
  type TextRunPrimitive,
} from "./cadPrimitive";
import { entityToPrimitives } from "./entityToPrimitives";
import {
  paintTextRuns,
  type TextPaintResult,
  type FontEmbedder,
} from "./textPainter";
import type { ResolveFontOptions } from "./fontResolve";
import {
  buildLayerVisibilityMap,
  collectViewportContent,
  isPaperEntityVisible,
  type VpClipRectMm,
} from "./viewportPlot";
import { resolvePlotRegion, type ResolvedPlotRegion } from "./plotRegion";
import {
  emitBatchedPaths,
  emitGradientHatch,
  emitHatchPattern,
} from "./batchPaint";
import {
  buildLayerLineweightMap,
  buildLayerLinetypeMap,
} from "./converters/resolveStyle";
import type { LinetypeSysvars } from "../../scene3d/rendering/linetypePatterns";
import { layerSections } from "./layerSections";
import { createOcgController, type PageOcgScope } from "./ocg";

/** Per-viewport accounting (PDF-3). */
export interface ViewportPlotStats {
  /** Total VIEWPORT entities on the layout (incl. control / off). */
  total: number;
  /** Control viewports skipped (viewportId === 1). */
  controlSkipped: number;
  /** Off / degenerate viewports skipped (status <= 0 / zero extent). */
  offSkipped: number;
  /** Viewports that emitted a clipped model block. */
  rendered: number;
  /** Model-space path primitives emitted across all rendered VPs. */
  modelPathCount: number;
  /** Model-space text runs emitted across all rendered VPs. */
  modelTextRunCount: number;
  /** VP border frames drawn (one per rendered VP on a plottable/visible layer). */
  framesDrawn: number;
}

/** Per-sheet stats returned by {@link renderSheetPage}. */
export interface SheetRenderStats {
  /** MediaBox dimensions in points (physical paper). */
  mediaBox: { widthPt: number; heightPt: number };
  /** Number of style groups (batched operator blocks) emitted. */
  groupCount: number;
  /** Geometry primitives (paths) emitted: paper-space + per-VP model. */
  pathCount: number;
  /** Recorded text runs (paper-space + per-VP model). */
  textRunCount: number;
  /** Accounting for the text-paint pass (TTF embed / SHX stroke). */
  text: TextPaintResult;
  /** Per-viewport accounting. */
  viewports: ViewportPlotStats;
  /** True when the resolved plot region was empty (blank page emitted §7.6). */
  empty: boolean;
  /** The resolved plot-area note (fallback / empty diagnostics), if any. */
  regionNote?: string;
}

/** Options for {@link renderSheetPage}. */
export interface RenderSheetOptions {
  /** Plot settings for this sheet (defaults applied by the caller). */
  settings: LayoutPlotSettings;
  /** Paper-space tessellation tolerance in mm (default 0.1). */
  toleranceMm?: number;
  /** Font-resolution overrides for the text pass. */
  fontResolve?: ResolveFontOptions;
  /**
   * Document-level optional-content (OCG / PDF-layer) controller (PDF-6.2b). A
   * multi-page PUBLISH passes ONE controller shared across all pages (OCGs and
   * `/OCProperties` are catalog-level) and `finalize()`s it once after the last
   * page. A single-sheet PLOT may omit it: `renderSheetPage` then creates a
   * controller for this one page and finalizes it before returning, so the
   * single page's catalog still gets `/OCProperties`.
   */
  ocgController?: import("./ocg").DocumentOcgController;
  /**
   * PDF-6.9b — layer NAMES the user un-checked in the "Include Layers" panel
   * of the PDF Options sub-dialog. The OCG layer-batch loop SKIPS any section /
   * hatch / VP frame whose layer is in this set: neither the OCG marked-content
   * wrapper nor the paint operators are emitted, so the layer contributes NO
   * geometry to the output PDF (and creates NO OCG entry, since OCGs are lazy).
   *
   * Empty / absent → every layer included (the pre-6.9b behaviour). Comparison
   * is case-sensitive to mirror how `layerSections` keys its sections (the
   * authored layer name flows through verbatim).
   */
  omitLayers?: ReadonlyArray<string>;
  /**
   * PDF-6.3b — the loaded CTB / STB plot-style table. Threaded into the
   * per-entity {@link CadConvertContext} so each style resolution applies the
   * matching entry's overrides (colour, lineweight, linetype, screening alpha,
   * do-not-plot). Absent → no overrides (byte-identical to PDF-6.3a output).
   */
  plotStyleTable?: PlotStyleTable;
  /**
   * PDF-6.4 — AutoCAD "Lines merge / Overwrite" PC3 plot setting. When
   * `"merge"`, every painted run (paths + hatches) is wrapped in `/BM /Multiply`
   * so overlapping geometry darkens. Default / absent → `"overwrite"`
   * (PDF Normal blend, byte-identical to pre-6.4 output).
   *
   * Threaded as a session-level option rather than per-LAYOUT-persist because
   * the `PlotLayoutFlags` bitfield has no Merge-Control bit and AutoCAD's PC3
   * file is a session / output-device setting.
   */
  mergeControl?: "overwrite" | "merge";
}

/**
 * Render one sheet into `pdf` (adds one page) and return its stats. The page is
 * sized to the sheet's PHYSICAL paper; the plot area / scale / centering are
 * resolved per the settings' `plotAreaType` (PDF-4).
 */
export async function renderSheetPage(
  pdf: PDFDocument,
  doc: DrawingDocument,
  space: DrawingSpace,
  options: RenderSheetOptions,
): Promise<SheetRenderStats> {
  const settings = options.settings;
  const toleranceMm = options.toleranceMm ?? DEFAULT_PAPER_TOLERANCE_MM;
  const layers = doc.styleTables?.layers ?? [];
  // PDF-6.4: Lines merge / Overwrite. Default "overwrite" so existing callers
  // (and the pre-6.4 byte-identity contract) are preserved without a change.
  const mergeControl: "overwrite" | "merge" =
    options.mergeControl ?? "overwrite";

  // PDF-6.1: drawing-level linetype-scale sysvars from the parsed DXF HEADER
  // ($LTSCALE / $PSLTSCALE / $MSLTSCALE). Threaded into the dash emitter so a
  // DASHED linetype plots at the authored scale (closes the PDF-5c LTSCALE
  // deferral). The per-entity CELTSCALE (DXF group 48) rides on each path's
  // PlotStyle.linetypeScale and is applied per-run inside `emitBatchedPaths`.
  // Defaults (1/1/1) preserve the prior solid-baseline behaviour byte-for-byte.
  const sysvars: LinetypeSysvars = {
    LTSCALE: typeof doc.ltscale === "number" && doc.ltscale > 0 ? doc.ltscale : 1,
    MSLTSCALE: typeof doc.msltscale === "number" ? doc.msltscale : 1,
    PSLTSCALE: typeof doc.psltscale === "number" ? doc.psltscale : 1,
  };

  // ── Converter context (layer colours + lineweights + block map). ──
  const ctx: CadConvertContext = {
    layerColors: buildLayerColorMap(
      layers.map((l: LayerDefinition) => ({
        name: l.name,
        color: String(l.color),
      })),
    ),
    // ByLayer lineweight resolution: an entity whose lineweight is ByLayer
    // (−1) plots at its layer's authored pen width, not the 0.25 mm Default.
    // The map carries the raw layer.lineweight code (resolveLineweightMm
    // decodes 0.01-mm units / special codes downstream in resolveStyle).
    layerLineweights: buildLayerLineweightMap(layers),
    // PDF-5c: ByLayer linetype resolution — an entity whose linetype is
    // ByLayer plots its layer's assigned dash pattern (default Continuous).
    layerLinetypes: buildLayerLinetypeMap(layers),
    blocks: doc.blocks ?? {},
    // PDF-5b: the DIMENSION converter resolves a dimension's referenced style's
    // DIMVARs from this table (DIMTXT / DIMASZ / DIMBLK …). Absent → defaults.
    dimensionStyles: doc.styleTables?.dimensionStyles ?? [],
    blockColor: null,
    toleranceMm,
    // PDF-6.3b: thread the loaded CTB/STB table into the per-entity style
    // resolver. Absent → resolver short-circuits, output byte-identical to
    // pre-6.3b. The LAYOUT-level `plotStyleName` (when STB) is the coarse
    // per-entity key the picker uses as a fallback when no layer-level name
    // applies (typed model has no per-entity field yet).
    ...(options.plotStyleTable ? { plotStyleTable: options.plotStyleTable } : {}),
    ...(settings.plotStyleName && settings.plotStyleName.trim() !== ""
      ? { layoutPlotStyleName: settings.plotStyleName }
      : {}),
    // PDF-6.4: thread the session-level merge control onto the converter
    // context. Converters don't consume it directly (the gs op is emitted by
    // `batchPaint` / `emitHatchPattern`); carrying it here keeps the field
    // co-located with the other plot-style knobs for downstream consumers.
    mergeControl,
    // @codex PR #785 round 12, F1 — the DOCUMENT's `$FILLMODE`. A wide
    // polyline's band is FILLED at 1 (the documented initial value) and
    // OUTLINED at 0, and until this thread existed the plot had no channel for
    // the variable at all and filled unconditionally — so a `$FILLMODE 0`
    // drawing plotted a solid ribbon that disagreed with both its own HEADER
    // and the canvas, which reads the same value through
    // `AdapterContext.fillmode`. Passed through verbatim (including an absent
    // one) because the readers apply the absent-means-1 rule themselves,
    // through the one shared `fillModePaintsSolid` predicate:
    // `converters/widePolylineBand.ts::bandPathPrimitive` (round 12) and
    // `converters/hatchConverter.ts::hatchToPrimitives` (round 16 P2 — a
    // solid, gradient or patterned HATCH plots as its stroked boundary at 0,
    // matching what the canvas shows).
    fillmode: doc.fillmode,
  };

  // Layer plot-visibility (PDF-3 §5): off / frozen / non-plottable layers are
  // filtered from BOTH paper space and per-VP model content (and Extents).
  const layerVis = buildLayerVisibilityMap(layers);

  // ── Plot-area resolution (PDF-4). Layout → origin0/scale1/no-centering. ──
  const region = resolvePlotRegion(settings, doc, space, {
    ctx,
    layers: layerVis,
  });

  // ── Page sized to PHYSICAL paper (NOT minus margins). ──
  const widthPt = settings.paperWidthMm * MM_TO_PT;
  const heightPt = settings.paperHeightMm * MM_TO_PT;
  const page = pdf.addPage([widthPt, heightPt]);

  // PDF-6.2b: optional-content (OCG / PDF-layer) controller. PUBLISH passes one
  // shared doc-level controller; a single-sheet PLOT omits it, so we create a
  // local one and finalize it before returning (this page's catalog still gets
  // `/OCProperties`). The per-page scope lazily creates an OCG the first time a
  // layer is wrapped (empty layers → no OCG) and assigns its page `/Properties`
  // key.
  const sharedController = options.ocgController;
  const ocgController = sharedController ?? createOcgController(pdf);
  const ocgScope = ocgController.pageScope(page);

  // PDF-6.9b: build the omit-layers Set once. Membership is exact-name (the
  // authored layer name as it flows through `layerSections`). An empty / absent
  // list keeps every layer (the pre-6.9b behaviour, fast path: the Set is
  // empty so `has()` is always false). The same Set is consulted by
  // `emitLayeredPaths` / `emitLayeredText` for paper-space + per-VP content,
  // and by the inline hatch + VP-frame paths (both files content under their
  // own marked-content scope outside `emitLayeredPaths`).
  const omitLayerSet: ReadonlySet<string> = new Set<string>(
    options.omitLayers ?? [],
  );

  const text: TextPaintResult = emptyTextResult();
  const viewports: ViewportPlotStats = {
    total: 0,
    controlSkipped: 0,
    offSkipped: 0,
    rendered: 0,
    modelPathCount: 0,
    modelTextRunCount: 0,
    framesDrawn: 0,
  };
  const mediaBox = { widthPt, heightPt };

  // §7.3 / §7.6: empty region → blank page, no geometry walk, warn via stats.
  if (region.empty) {
    // No layer was wrapped on a blank page → finalize() is a no-op (no OCGs).
    // We still finalize a locally-created controller so the contract (caller
    // owns finalize for a shared controller; we own it for a local one) holds.
    if (!sharedController) ocgController.finalize();
    return {
      mediaBox,
      groupCount: 0,
      pathCount: 0,
      textRunCount: 0,
      text,
      viewports,
      empty: true,
      regionNote: region.note,
    };
  }

  // Build the page transform with the resolved region origin + (for fit
  // Window/Extents) scale override + centering. For Layout these are origin
  // (0,0) / undefined scale / (0,0) centering → byte-identical to PDF-1/2/3.
  const isLayoutArea =
    region.scale === 1 &&
    region.regionOrigin.x === 0 &&
    region.regionOrigin.y === 0 &&
    region.centeringOffsetMm.x === 0 &&
    region.centeringOffsetMm.y === 0;
  const pageTransform = isLayoutArea
    ? buildPdfTransform({ settings })
    : buildPdfTransform({
        settings,
        regionOrigin: region.regionOrigin,
        scaleOverride: region.scale,
        centeringOffsetMm: region.centeringOffsetMm,
      });

  const textStyleMap = buildTextStyleMap(doc.styleTables?.textStyles ?? []);
  const embedder = pdf as unknown as FontEmbedder;

  let groupCount = 0;
  let paperPathCount = 0;
  let paperTextRunCount = 0;

  let pendingPaths: PathPrimitive[] = [];
  let pendingText: TextRunPrimitive[] = [];

  const flushPaper = async (): Promise<void> => {
    if (pendingPaths.length > 0) {
      // FIX C (Codex #180): thread the LAYOUT fit scale into the lineweight
      // calc so ScaleLineweights pens track the plotted scale on a fitted
      // Window/Extents/Limits plot. Layout = scale 1 → unchanged.
      // PDF-5c: paper-space dash lengths (mm) scale by the same layout fit
      // (region.scale) so a fitted plot's DASHED lines track the plotted scale.
      // PDF-6.2b: segment by ORDERED layer section and wrap each in its layer's
      // OCG (`/OC /OCn BDC … EMC`); the per-section batching + draw order are
      // unchanged inside each wrapper.
      groupCount += emitLayeredPaths(page, ocgScope, pendingPaths, omitLayerSet, (items) =>
        emitBatchedPaths(page, items, pageTransform, settings, region.scale, {
          dashScale: region.scale,
          viewportKind: "paper",
          sysvars,
          // PDF-6.4: Lines merge wraps each painted run in `/BM /Multiply`.
          mergeControl,
        }),
      );
    }
    if (pendingText.length > 0) {
      // FIX A (Codex #180): paper-space TEXT must scale by the plot region's
      // fit scale, same as the geometry (mirrors the PDF-3 VP-text fix). For
      // Layout (scale 1) this is MM_TO_PT → byte-identical to before.
      // PDF-6.2b: text is also segmented by ORDERED layer section + OCG-wrapped.
      await emitLayeredText(page, ocgScope, pendingText, omitLayerSet, async (items) => {
        const r = await paintTextRuns(
          page,
          embedder,
          items,
          pageTransform,
          settings,
          {
            textStyles: textStyleMap,
            fontResolve: options.fontResolve,
            fontkit,
            heightToPt: region.scale * MM_TO_PT,
            // PDF-6.4: paper-space text honours the same Lines-merge wrap as
            // paths/hatches (each run gets `/BM /Multiply` in merge mode).
            mergeControl,
          },
        );
        mergeTextResult(text, r);
      });
    }
    pendingPaths = [];
    pendingText = [];
  };

  /**
   * Emit a paper-space PATTERN hatch (PDF-5a). The hatch is its own clip+strokes
   * block, so we FLUSH pending paths/text first (draw order: prior geometry
   * paints before the hatch) then emit the self-contained block. PDF-6.2b: the
   * whole hatch block is wrapped in its layer's OCG (`/OC /OCn BDC … EMC`).
   */
  const emitPaperHatch = async (
    hatch: HatchPatternPrimitive,
  ): Promise<void> => {
    await flushPaper();
    // PDF-6.9b: skip the hatch entirely (no OCG wrap, no clip+strokes block) if
    // its layer is in the omit set — the user un-checked it in PDF Options.
    if (omitLayerSet.has(hatch.layer)) return;
    // PDF-6.3b: defence-in-depth — drop a CTB/STB do-not-plot hatch even if the
    // converter forgot to short-circuit on `style.skip`.
    if ((hatch.style as { skip?: boolean }).skip) return;
    const mc = ocgScope.markedContent(hatch.layer);
    page.pushOperators(mc.begin);
    groupCount += emitHatchPattern(page, hatch, pageTransform, settings, {
      // PDF-6.4: pattern hatch honours the same Lines-merge wrapper as paths.
      mergeControl,
    });
    page.pushOperators(mc.end);
  };

  /**
   * Emit a paper-space GRADIENT hatch (PDF-6.5b). Same dispatch shape as
   * {@link emitPaperHatch}: flush pending paths/text (draw order), honour the
   * omit-layer + do-not-plot gates, wrap the self-contained clip + `sh` block in
   * its layer's OCG marked content.
   */
  const emitPaperGradientHatch = async (
    hatch: GradientHatchPrimitive,
  ): Promise<void> => {
    await flushPaper();
    if (omitLayerSet.has(hatch.layer)) return;
    if ((hatch.style as { skip?: boolean }).skip) return;
    const mc = ocgScope.markedContent(hatch.layer);
    page.pushOperators(mc.begin);
    groupCount += emitGradientHatch(page, hatch, pageTransform, settings, {
      // PDF-6.4: gradient hatch honours the same Lines-merge wrapper as paths.
      mergeControl,
    });
    page.pushOperators(mc.end);
  };

  // FIX B (Codex #180): for a sub-region plot (NOT Layout) clip the WHOLE
  // sheet body — paper geometry + paper text + VP blocks + VP frames — to the
  // resolved region rectangle, so stray geometry just outside a Window/Limits
  // window does not bleed onto the page (the page itself only clips at the
  // physical sheet). `isLayoutArea` covers Layout + the View/Display Layout
  // fallbacks (scale 1 / origin 0) → no extra clip there (full sheet).
  const useRegionClip = !isLayoutArea;
  if (useRegionClip) emitRegionClipOpen(page, region, pageTransform);

  for (const entity of space.entities) {
    if (entity.type === "Viewport") {
      const vp = entity as ViewportEntity;
      viewports.total += 1;
      // Flush paper authored BEFORE this VP (draw-order interleave).
      await flushPaper();

      const content = collectViewportContent(vp, doc, {
        pageTransform,
        ctx,
        layers: layerVis,
      });
      if (content.skipped) {
        if (content.skipReason === "control") viewports.controlSkipped += 1;
        else viewports.offSkipped += 1;
        continue;
      }
      viewports.rendered += 1;
      viewports.modelPathCount += content.paths.length;
      viewports.modelTextRunCount += content.textRuns.length;

      // PDF-6.2b: the VP clip `q … W n … Q` brackets ALL the VP model content;
      // the per-layer `/OC … BDC … EMC` wrappers sit STRICTLY INSIDE the clip
      // (each BDC/EMC pair balances within the same `q…Q`), so the optional
      // content is both clipped to the VP and toggled by its layer's OCG. The
      // global OCG is SHARED with paper-space content on the same layer — a
      // layer drawn both in paper space and through a VP is ONE PDF layer.
      emitVpClipOpen(page, content.clipRectMm, pageTransform);
      // FIX C (Codex #180): a fitted layout region scales the VP block's paper
      // footprint too, so its pens track the layout fit scale (region.scale).
      // Layout = 1 → unchanged. The per-VP model→paper scale is independent and
      // already baked into `content.transform` (geometry), not the lineweight.
      // PDF-5c: VP model dash lengths are in MODEL mm; the dash array is emitted
      // in final pt (not via a PDF `cm`), so it must absorb the model→paper
      // ratio (content.unitToPaperScale) AND the layout fit (region.scale) —
      // mirroring how the geometry transform composes both.
      groupCount += emitLayeredPaths(page, ocgScope, content.paths, omitLayerSet, (items) =>
        emitBatchedPaths(page, items, content.transform, settings, region.scale, {
          dashScale: content.unitToPaperScale * region.scale,
          viewportKind: "model",
          sysvars,
          // PDF-6.4: VP-model paths get the same Lines-merge gs wrap as paper.
          mergeControl,
        }),
      );
      // Model-space PATTERN hatches (PDF-5a): each is its own nested clip+strokes
      // block, intersecting the hatch boundary with the outer VP clip. Each is
      // wrapped in its layer's OCG (still inside the VP clip `q…Q`).
      for (const hatch of content.hatchPatterns) {
        // PDF-6.9b: skip an omitted-layer hatch (same gate as paper-space hatches).
        if (omitLayerSet.has(hatch.layer)) continue;
        // PDF-6.3b: do-not-plot defence-in-depth (same as paper-space hatch).
        if ((hatch.style as { skip?: boolean }).skip) continue;
        const hmc = ocgScope.markedContent(hatch.layer);
        page.pushOperators(hmc.begin);
        groupCount += emitHatchPattern(
          page,
          hatch,
          content.transform,
          settings,
          {
            // PDF-6.4: VP-model pattern hatch gets the same Lines-merge wrap.
            mergeControl,
          },
        );
        page.pushOperators(hmc.end);
      }
      // Model-space GRADIENT hatches (PDF-6.5b): each is its own nested clip +
      // `sh` shading block, intersecting the hatch boundary with the outer VP
      // clip. Each is wrapped in its layer's OCG (still inside the VP clip).
      for (const hatch of content.gradientHatches) {
        if (omitLayerSet.has(hatch.layer)) continue;
        if ((hatch.style as { skip?: boolean }).skip) continue;
        const gmc = ocgScope.markedContent(hatch.layer);
        page.pushOperators(gmc.begin);
        groupCount += emitGradientHatch(
          page,
          hatch,
          content.transform,
          settings,
          {
            // PDF-6.4: VP-model gradient hatch gets the same Lines-merge wrap.
            mergeControl,
          },
        );
        page.pushOperators(gmc.end);
      }
      if (content.textRuns.length > 0) {
        await emitLayeredText(page, ocgScope, content.textRuns, omitLayerSet, async (items) => {
          const r = await paintTextRuns(
            page,
            embedder,
            items,
            content.transform,
            settings,
            {
              textStyles: textStyleMap,
              fontResolve: options.fontResolve,
              fontkit,
              heightToPt: content.unitToPaperScale * MM_TO_PT,
              extraRotationDeg: -content.twistDeg,
              // PDF-6.4: VP-model text gets the same Lines-merge wrap as paper.
              mergeControl,
            },
          );
          mergeTextResult(text, r);
        });
      }
      emitVpClipClose(page);

      if (isPaperEntityVisible(vp.layer, layerVis) && !omitLayerSet.has(vp.layer)) {
        // PDF-6.9b: an omitted VP layer also drops the frame. The VP's MODEL
        // content is already filtered at the per-section gate inside
        // `emitLayeredPaths` / `emitLayeredText`, so the visible VP block may
        // still appear empty inside its clip — but the frame (a paper-space
        // marker) is suppressed alongside the rest of the VP's layer content.
        const frameRgb = resolvePlotRgb(
          // EPIC-COLOR-UNION S1 — project the typed VP colour to the legacy
          // plot string the resolver consumes.
          colorToLegacyString(coerceColor(vp.color)),
          vp.layer,
          ctx.layerColors,
          ctx.blockColor,
        );
        // PDF-6.2b: the VP frame is the VIEWPORT entity itself, so it files
        // under the VP's own layer OCG (a separate `q…S…Q` block OUTSIDE the VP
        // clip, wrapped in the VP layer's marked content).
        const fmc = ocgScope.markedContent(vp.layer);
        page.pushOperators(fmc.begin);
        emitVpFrame(page, content.clipRectMm, pageTransform, frameRgb, HAIRLINE_PT);
        page.pushOperators(fmc.end);
        viewports.framesDrawn += 1;
      }
      continue;
    }

    if (!isPaperEntityVisible(entity.layer, layerVis)) continue;
    for (const p of entityToPrimitives(entity, ctx)) {
      if (p.type === "path") {
        pendingPaths.push(p);
        paperPathCount += 1;
      } else if (p.type === "hatch-pattern") {
        // Pattern hatch is its own clip block — emit it now (flushes pending
        // paper geometry first to preserve authored draw order).
        await emitPaperHatch(p);
      } else if (p.type === "gradient-hatch") {
        // Gradient hatch is its own clip + `sh` block — same draw-order flush.
        await emitPaperGradientHatch(p);
      } else {
        pendingText.push(p);
        paperTextRunCount += 1;
      }
    }
  }

  await flushPaper();

  // Close the page-level region clip (FIX B) after all sheet content.
  if (useRegionClip) emitRegionClipClose(page);

  // PDF-6.2b: a SINGLE-sheet PLOT owns the controller we created locally, so we
  // write `/OCProperties` now. A PUBLISH passes a SHARED controller and
  // finalizes once after the last page (so all pages' OCGs land in one catalog
  // entry) — we must NOT finalize it here.
  if (!sharedController) ocgController.finalize();

  return {
    mediaBox,
    groupCount,
    pathCount: paperPathCount + viewports.modelPathCount,
    textRunCount: paperTextRunCount + viewports.modelTextRunCount,
    text,
    viewports,
    empty: false,
    regionNote: region.note,
  };
}

/** A zeroed {@link TextPaintResult} accumulator. */
export function emptyTextResult(): TextPaintResult {
  return {
    renderedRuns: 0,
    ttfRuns: 0,
    shxRuns: 0,
    embeddedFonts: 0,
    shxPathOps: 0,
    substitutedRuns: 0,
    fallbackRuns: 0,
    subsetRetries: 0,
  };
}

/** Fold a per-segment {@link TextPaintResult} into the running accumulator. */
function mergeTextResult(acc: TextPaintResult, add: TextPaintResult): void {
  acc.renderedRuns += add.renderedRuns;
  acc.ttfRuns += add.ttfRuns;
  acc.shxRuns += add.shxRuns;
  acc.embeddedFonts += add.embeddedFonts;
  acc.shxPathOps += add.shxPathOps;
  acc.substitutedRuns += add.substitutedRuns;
  acc.fallbackRuns += add.fallbackRuns;
  acc.subsetRetries += add.subsetRetries;
}

/**
 * PDF-6.2b: emit a path list segmented into ORDERED per-layer sections, each
 * wrapped in its layer's OCG marked content (`/OC /OCn BDC … EMC`).
 *
 * `layerSections` groups CONSECUTIVE same-layer paths into runs PRESERVING the
 * authored order (an A/B/A draw sequence → three sections, NOT bucketed) so the
 * paint order — and therefore z-order of overlapping / filled geometry — is
 * untouched. For each section we push the layer's `BDC`, run the supplied
 * `emit` (the existing run-length `emitBatchedPaths` over JUST this section's
 * paths), then push `EMC`. Returns the total group count `emit` reported.
 *
 * The BDC/EMC operators add NO geometry: the m/l/c/S/B/f operators inside are
 * byte-for-byte what the un-wrapped batch emitted; only the marked-content
 * brackets are new. BDC/EMC pairs always balance (one EMC per BDC) and never
 * cross a `q…Q` boundary (a section is wrapped entirely within the current
 * graphics-state scope).
 */
function emitLayeredPaths(
  page: PDFPage,
  scope: PageOcgScope,
  paths: readonly PathPrimitive[],
  omitLayers: ReadonlySet<string>,
  emit: (items: PathPrimitive[]) => number,
): number {
  let groups = 0;
  for (const section of layerSections(paths)) {
    // PDF-6.9b: an omitted layer skips BOTH the OCG wrapper AND the geometry
    // emission — neither `markedContent` is invoked (so no OCG is lazily
    // created for this layer) nor `emit` runs (so no path operators land on
    // the page). Other sections are emitted unchanged.
    if (omitLayers.has(section.layer)) continue;
    // PDF-6.3b: defence-in-depth — filter primitives whose CTB/STB style
    // marked them do-not-plot. The converters already short-circuit on
    // `style.skip`, but a future converter forgetting the gate would silently
    // leak skipped geometry; this guard ensures the do-not-plot contract holds
    // at the emit boundary too. Filters BEFORE OCG creation so a section that
    // is wholly skipped (every primitive flagged) does NOT lazily create the
    // layer's OCG entry — matching the omit-layers semantics above.
    const items = (section.items as PathPrimitive[]).filter(
      (p) => !(p.style as { skip?: boolean }).skip,
    );
    if (items.length === 0) continue;
    const mc = scope.markedContent(section.layer);
    page.pushOperators(mc.begin);
    groups += emit(items);
    page.pushOperators(mc.end);
  }
  return groups;
}

/**
 * PDF-6.2b: emit a text-run list segmented into ORDERED per-layer sections, each
 * wrapped in its layer's OCG marked content (`/OC /OCn BDC … EMC`). Mirrors
 * {@link emitLayeredPaths} for the text pass: each section's `BT … Tj … ET`
 * (TTF) or stroke glyph (SHX) operators are unchanged; only the marked-content
 * brackets wrap them. Awaits the async `emit` (TTF embedding is async).
 */
async function emitLayeredText(
  page: PDFPage,
  scope: PageOcgScope,
  runs: readonly TextRunPrimitive[],
  omitLayers: ReadonlySet<string>,
  emit: (items: TextRunPrimitive[]) => Promise<void>,
): Promise<void> {
  for (const section of layerSections(runs)) {
    // PDF-6.9b: same gate as `emitLayeredPaths` — omitted-layer text is not
    // wrapped in an OCG and not painted (neither BT…ET nor stroke glyphs).
    if (omitLayers.has(section.layer)) continue;
    // PDF-6.3b: defence-in-depth — filter text runs flagged do-not-plot by
    // the CTB/STB style. Mirrors `emitLayeredPaths`. An emptied section skips
    // OCG creation so a wholly-do-not-plot layer leaves no OCG either.
    const items = (section.items as TextRunPrimitive[]).filter(
      (t) => !t.skip,
    );
    if (items.length === 0) continue;
    const mc = scope.markedContent(section.layer);
    page.pushOperators(mc.begin);
    await emit(items);
    page.pushOperators(mc.end);
  }
}

/**
 * Open a page-level clip to the resolved plot REGION (FIX B, Codex #180). For
 * a sub-region plot (Window / Limits, and Extents for correctness) geometry,
 * text and VP blocks just OUTSIDE the region rectangle must be clipped away —
 * the page otherwise only clips at the physical sheet, so stray geometry near
 * the window still appears. The region rect corners are in PLOTTED-space mm
 * (the region's lower-left = `regionOrigin`, extent = `region.width/height`);
 * we map them through `pageTransform` and emit `q · m/l×3 · h · W n` (mirrors
 * {@link emitVpClipOpen}). Closed with {@link emitRegionClipClose} (`Q`).
 */
function emitRegionClipOpen(
  page: PDFPage,
  region: ResolvedPlotRegion,
  pageTransform: PdfTransform,
): void {
  const x0 = region.regionOrigin.x;
  const y0 = region.regionOrigin.y;
  const x1 = x0 + region.region.width;
  const y1 = y0 + region.region.height;
  const pLL = pageTransform(x0, y0);
  const pLR = pageTransform(x1, y0);
  const pUR = pageTransform(x1, y1);
  const pUL = pageTransform(x0, y1);
  page.pushOperators(
    pushGraphicsState(),
    moveTo(pLL[0], pLL[1]),
    lineTo(pLR[0], pLR[1]),
    lineTo(pUR[0], pUR[1]),
    lineTo(pUL[0], pUL[1]),
    closePath(),
    clip(),
    endPath(),
  );
}

/** Close the page-level region clip (`Q`). */
function emitRegionClipClose(page: PDFPage): void {
  page.pushOperators(popGraphicsState());
}

/**
 * Open a viewport clip: `q` then the VP rectangle as a closed path, then `W n`.
 */
function emitVpClipOpen(
  page: PDFPage,
  rect: VpClipRectMm,
  pageTransform: PdfTransform,
): void {
  const [ll, lr, ur, ul] = rect.corners;
  const pLL = pageTransform(ll.x, ll.y);
  const pLR = pageTransform(lr.x, lr.y);
  const pUR = pageTransform(ur.x, ur.y);
  const pUL = pageTransform(ul.x, ul.y);
  page.pushOperators(
    pushGraphicsState(),
    moveTo(pLL[0], pLL[1]),
    lineTo(pLR[0], pLR[1]),
    lineTo(pUR[0], pUR[1]),
    lineTo(pUL[0], pUL[1]),
    closePath(),
    clip(),
    endPath(),
  );
}

/** Close a viewport clip (`Q`). */
function emitVpClipClose(page: PDFPage): void {
  page.pushOperators(popGraphicsState());
}

/**
 * Emit the VP border frame: a stroked rectangle using the same paper-mm corners
 * as the clip rect, in its own `q … S Q` block (OUTSIDE the VP clip).
 */
function emitVpFrame(
  page: PDFPage,
  rect: VpClipRectMm,
  pageTransform: PdfTransform,
  color: RgbColor,
  lwPt: number,
): void {
  const [ll, lr, ur, ul] = rect.corners;
  const pLL = pageTransform(ll.x, ll.y);
  const pLR = pageTransform(lr.x, lr.y);
  const pUR = pageTransform(ur.x, ur.y);
  const pUL = pageTransform(ul.x, ul.y);
  page.pushOperators(
    pushGraphicsState(),
    setLineWidth(lwPt),
    setStrokingRgbColor(color.r, color.g, color.b),
    moveTo(pLL[0], pLL[1]),
    lineTo(pLR[0], pLR[1]),
    lineTo(pUR[0], pUR[1]),
    lineTo(pUL[0], pUL[1]),
    closePath(),
    stroke(),
    popGraphicsState(),
  );
}

/** Build a `styleName → TextStyle` lookup (case-sensitive, AutoCAD names). */
function buildTextStyleMap(
  styles: readonly TextStyle[],
): ReadonlyMap<string, TextStyle> {
  const map = new Map<string, TextStyle>();
  for (const s of styles) map.set(s.name, s);
  return map;
}
